import invariant from 'invariant';

import { useEmbedSearchWidget } from 'modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { EmbedSearchWidgetLayoutHandleProps } from 'modules/embedSearchWidget/layouts/layout/EmbedSearchWidgetLayoutHandleProps';
import type { embedSearchWidgetViewVolopRoute } from 'modules/embedSearchWidget/routing/embedSearchWidget.routes';
import { listingApiHorizonFetchVolop } from 'modules/listing/volops/api/listingApiFetchVolop';
import { SearchVolopsRefinements } from 'modules/search/pages/Volops/components/SearchVolopsRefinements';
import { userDashboardApiIsomorphicFetchListingExtras } from 'modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from 'routing/types/HorizonAppRouteComponentProps';

import { EmbedSearchWidgetViewVolopPageLoadable } from './EmbedSearchWidgetViewVolopPageLoadable';

export const handle = {
  searchRefinements: <SearchVolopsRefinements />,
  defaultSearchType: 'VOLOP',
  isSearchPage: false,
  searchFromPageType: 'LISTING',
} satisfies EmbedSearchWidgetLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { volopId },
}: HorizonAppRouteLoaderArgs<typeof embedSearchWidgetViewVolopRoute>) {
  try {
    const [volop, listingExtras] = await Promise.all([
      listingApiHorizonFetchVolop({ request, context })(volopId),
      userDashboardApiIsomorphicFetchListingExtras({ request, context })([
        volopId,
      ]),
      EmbedSearchWidgetViewVolopPageLoadable.load(),
    ]);

    const listingApplicationId = listingExtras[volopId]?.applicationId ?? null;

    return { volop, listingApplicationId };
  } catch {
    return { volop: null };
  }
}

export default function EmbedSearchWidgetViewVolopHorizonRoute({
  loaderData: { volop, listingApplicationId },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { embedSearchWidget } = useEmbedSearchWidget();
  invariant(embedSearchWidget, 'Embed search widget not set.');

  if (!volop || volop.type !== 'VOLOP') return <NotFoundPageLoadable />;

  return (
    <EmbedSearchWidgetViewVolopPageLoadable
      volop={volop}
      embedSearchWidget={embedSearchWidget}
      listingApplicationId={listingApplicationId}
    />
  );
}
